import { customerKeys } from "./customer-keys";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useSession } from "@/services/auth0/session";
import axios from "axios";

// Account-credit LEDGER client (ezx-server #343): the wallet moved off the
// Stripe customer balance into an internal append-only ledger. This is the
// single client for all credit endpoints — the duplicate wrappers that
// lived in api/payments.ts were deleted with it.

export interface CreditBalance {
  customerId: string;
  /** Can be NEGATIVE after a deposit chargeback — render honestly. */
  balanceCents: number;
  /** Wallet frozen while a deposit-charge dispute is open; spends reject. */
  frozen: boolean;
}

export type LedgerEntryType =
  | "DEPOSIT"
  | "MANUAL_DEPOSIT"
  | "ORDER_PAYMENT"
  | "REFUND"
  | "ADMIN_ADJUSTMENT"
  | "DEPOSIT_REVERSAL"
  | "MIGRATION";

export interface LedgerEntry {
  id: string;
  /** Signed: + money in, − money out. */
  amountCents: number;
  type: LedgerEntryType;
  reason: string;
  createdAt: string;
  orderId: string | null;
  balanceAfterCents: number;
  manualDepositMethod?: "CASH" | "E_TRANSFER" | null;
  reference?: string | null;
}

interface LedgerResponse {
  customerId: string;
  balanceCents: number;
  entries: LedgerEntry[];
  nextCursor: string | null;
}

interface DepositCreditRequest {
  amountCents: number;
  /** Mandatory: a deposit only exists with a real card charge behind it. */
  paymentMethodId: string;
  description?: string;
}

interface DepositCreditResponse {
  success: boolean;
  balanceCents: number;
  depositedCents: number;
}

export interface CustomerPaymentMethod {
  id: string;
  type: string;
  last4: string;
  brand: string | null;
  expiryMonth: number | null;
  expiryYear: number | null;
  isDefault: boolean;
}

export function useGetCustomerCreditBalance(customerId: string) {
  const { token } = useSession();

  return useQuery<CreditBalance>({
    queryKey: ["customer-credit-balance", customerId],
    queryFn: async () => {
      const { data } = await axios.get<CreditBalance>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/credit/balance`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!customerId && !!token,
  });
}

/** Cursor-paginated ledger statement — pages must be loaded through
 * `fetchNextPage` so long histories aren't silently truncated at `take`. */
export function useGetCustomerCreditLedger(customerId: string, take = 50) {
  const { token } = useSession();

  return useInfiniteQuery<LedgerResponse>({
    queryKey: ["customer-credit-transactions", customerId, take],
    queryFn: async ({ pageParam }) => {
      const { data } = await axios.get<LedgerResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/credit/transactions`,
        {
          params: { take, ...(pageParam ? { cursor: pageParam } : {}) },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: !!customerId && !!token,
  });
}

/** Customer's saved cards — the deposit dialog's payment-method picker. */
export function useGetCustomerPaymentMethods(customerId: string) {
  const { token } = useSession();

  return useQuery<{ paymentMethods: CustomerPaymentMethod[] }>({
    queryKey: ["customer-payment-methods", customerId],
    queryFn: async () => {
      const { data } = await axios.get<{
        paymentMethods: CustomerPaymentMethod[];
      }>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/payment-methods`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    enabled: !!customerId && !!token,
  });
}

export function useDepositCustomerCredit(
  customerId: string,
  options?: {
    onSuccess?: (data: DepositCreditResponse) => void;
    onError?: (error: Error) => void;
  }
) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation<DepositCreditResponse, Error, DepositCreditRequest>({
    mutationFn: async (data) => {
      const response = await axios.post<DepositCreditResponse>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/credit/deposit`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: customerKeys.all });
      void queryClient.invalidateQueries({
        queryKey: ["customer-credit-balance", customerId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["customer-credit-transactions", customerId],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

/** Dispute-evidence bundle (admin): downloads everything needed to respond
 * to a deposit-charge chargeback in the Stripe dashboard. */
export async function downloadCreditDisputeEvidence(
  customerId: string,
  token: string
): Promise<void> {
  const { data } = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/credit/dispute-evidence`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `credit-dispute-evidence-${customerId}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export interface ManualDepositRequest {
  amountCents: number;
  method: "CASH" | "E_TRANSFER";
  reference: string;
  note?: string;
  idempotencyKey: string;
  received: true;
}
export function useManualCustomerDeposit(customerId: string) {
  const { token } = useSession();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: ManualDepositRequest) => {
      const { data } = await axios.post<
        DepositCreditResponse & {
          alreadyRecorded: boolean;
          transactionId: string;
        }
      >(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customerId}/credit/manual-deposit`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: customerKeys.all });
      void client.invalidateQueries({
        queryKey: ["customer-credit-balance", customerId],
      });
      void client.invalidateQueries({
        queryKey: ["customer-credit-transactions", customerId],
      });
    },
  });
}
